// FormAddEditUsers.jsx

import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateUsers } from '../../hooks/Users/useCreateUsers';
import { useUpdateUsers } from '../../hooks/Users/useUpdateUsers';
import FormInput from '../inputs/FormInput';
import { useGetRoles } from '../../hooks/roles/useGetRoles';
import LoadingPage from '../global/LoadingPage';
import { toast } from 'react-hot-toast';
import { useUploadMedia } from '../../hooks/media/useUploadMedia';
import { countries } from '../../constants/countres';
import {
    formatFullPhoneNumber,
    validatePhoneNumber,
    parseFullPhoneNumber,
} from '../../utils/phoneUtils';

const FormAddEditUsers = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const { isLoading, roles } = useGetRoles(1, 999);
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    // ✅ State منفصل لرقم الهاتف
    const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState('');

    const [imageError, setImageError] = useState('');
    const [imageUploadedName, setImageUploadedName] = useState('');

    const { createUsers, isPending: isCreating } = useCreateUsers();
    const { editUsers, isUpdate } = useUpdateUsers();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        name: '',
        user_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        is_admin: false,
        status: true,
        roles_id: [],
        image: '',
        image_preview: '',
    });

    // ✅ mediaId لاستخدامه في زر الحذف داخل InputFileUpload
    const mediaId = currentData?.image?.id ?? null;

    useEffect(() => {
        if (!isEdit || !currentData) return;

        // ✅ فصل رقم الهاتف عند التعديل
        if (currentData.phone) {
            const { countryPhone, nationalNumber } = parseFullPhoneNumber(currentData.phone);
            setSelectedCountryCode(countryPhone);
            setPhoneNumber(nationalNumber);
        }

        setForm((prev) => ({
            ...prev,
            name: currentData.name || '',
            user_name: currentData.user_name || '',
            email: currentData.email || '',
            password: '',
            password_confirmation: '',
            is_admin: currentData.is_admin ?? false,
            status: currentData.status ?? true,
            roles_id: currentData.roles ? currentData.roles.map((role) => Number(role.id)) : [],
            image: currentData.image?.file_name || '',
            image_preview: currentData.image?.url || currentData.image?.preview || '',
        }));

        setImageUploadedName(currentData.image?.file_name || '');
        setImageError('');
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            setForm((prev) => ({ ...prev, [name]: files[0] || null }));
            return;
        }

        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        setImageError('');
        setImageUploadedName('');

        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        uploadMedia(formData, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || '';
                if (!fileName) {
                    setImageError(
                        isArabic
                            ? 'تعذر الحصول على اسم الملف من الخادم'
                            : 'Cannot get uploaded file name from server'
                    );
                    return;
                }
                setImageUploadedName(fileName);
                // نخزن اسم الملف لاستخدامه في الـ payload
                setForm((prev) => ({ ...prev, image: fileName }));
            },
            onError: () => {
                setImageError(
                    isArabic
                        ? 'فشل رفع الصورة، الرجاء المحاولة مرة أخرى'
                        : 'Failed to upload image, please try again'
                );
            },
        });
    };

    const handleRolesChange = (event) => {
        const { value } = event.target;
        setForm((prev) => ({ ...prev, roles_id: value }));
    };

    const isDark = theme.palette.mode === 'dark';

    const handleSubmit = (e) => {
        e.preventDefault();

        // ✅ التحقق من صحة رقم الهاتف
        const phoneValidation = validatePhoneNumber(selectedCountryCode, phoneNumber, lng);

        if (!phoneValidation.isValid) {
            toast.error(phoneValidation.message);
            return;
        }

        // ✅ تكوين الرقم الكامل
        const fullPhone = formatFullPhoneNumber(selectedCountryCode, phoneNumber);

        if (form.password !== form.password_confirmation) {
            toast.error(
                isArabic
                    ? 'كلمة المرور وتأكيدها غير متطابقين'
                    : 'Password and confirmation do not match'
            );
            return;
        }

        if (isEdit && currentData?.id) {
            const payload = {
                name: form.name,
                user_name: form.user_name,
                email: form.email,
                phone: fullPhone, // ✅ الرقم الكامل
                ...(form.password && {
                    password: form.password,
                    password_confirmation: form.password_confirmation,
                }),
                is_admin: form.is_admin ? 1 : 0,
                status: !!form.status,
                roles_id: form.roles_id.map((id) => Number(id)),

                // ✅ لا تُرسل image نهائياً لو مفيش قيمة (أو اتحذفت)
                ...(form.image ? { image: form.image } : {}),
            };

            editUsers({ req: payload, id: currentData.id });
        } else {
            const formData = new FormData();
            formData.append('name', form.name);
            formData.append('user_name', form.user_name);
            formData.append('email', form.email);
            formData.append('phone', fullPhone); // ✅ الرقم الكامل
            formData.append('password', form.password);
            formData.append('password_confirmation', form.password_confirmation);
            formData.append('is_admin', form.is_admin ? 1 : 0);
            formData.append('status', form.status ? 1 : 0);
            form.roles_id.forEach((id, index) => {
                formData.append(`roles_id[${index}]`, Number(id));
            });

            // ✅ في الـ create نفس السلوك القديم: لا نضيف image إلا لو موجودة
            if (form.image) {
                formData.append('image', form.image);
            }

            createUsers(formData);
        }
    };

    if (isLoading) return <LoadingPage />;

    const rolesOptions = roles ?? [];
    const rolesNames = JSON.parse(localStorage.getItem('roles_names') || '[]');
    const isSuperAdmin = rolesNames.includes('super_admin');

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <FormInput
                        required={false}
                        label={isArabic ? 'الصورة الشخصية' : 'Avatar'}
                        type="file"
                        name="image"
                        value={form.image_preview}
                        onChange={handleImageChange}
                        labelImage={isArabic ? 'اختر صورة' : 'Choose image'}
                        labelBioImage={
                            isArabic
                                ? 'اسحب وأسقط الصورة هنا أو انقر للاختيار'
                                : 'Drag & drop or click to upload'
                        }
                        isUploading={isUploading}
                        errorMessage={imageError}
                        successMessage={
                            imageUploadedName && !imageError
                                ? isArabic
                                    ? 'تم رفع الصورة بنجاح'
                                    : 'Image uploaded successfully'
                                : ''
                        }
                        sx={{ mx: 'auto' }}
                        // ✅ تفعيل زر الحذف في حالة edit
                        mediaId={mediaId}
                        onDeleted={() => {
                            // عند الحذف من InputFileUpload (سيرفر/محلي): صفّر قيم الصورة
                            setForm((prev) => ({
                                ...prev,
                                image: '',
                                image_preview: '',
                            }));
                            setImageUploadedName('');
                            setImageError('');
                        }}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الاسم' : 'Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم المستخدم' : 'Username'}
                            placeholder={isArabic ? 'ادخل اسم المستخدم' : 'Enter username'}
                            type="text"
                            name="user_name"
                            value={form.user_name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'البريد الإلكتروني' : 'Email'}
                            placeholder="admin@example.com"
                            type="text"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* ✅ حقل رقم الهاتف الصحيح */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'رقم الجوال' : 'Phone'}
                            placeholder="+201000000000"
                            type="tel"
                            name="phone"
                            phoneNumber={phoneNumber}
                            setPhoneNumber={setPhoneNumber}
                            selectedCountryCode={selectedCountryCode}
                            setSelectedCountryCode={setSelectedCountryCode}
                            fullPhoneNumber={isEdit ? currentData?.phone : null}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كلمة المرور' : 'Password'}
                            placeholder={isArabic ? 'ادخل كلمة المرور' : 'Enter password'}
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            required={!isEdit}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'تأكيد كلمة المرور' : 'Confirm password'}
                            placeholder={
                                isArabic ? 'ادخل تأكيد كلمة المرور' : 'Enter confirm password'
                            }
                            type="password"
                            name="password_confirmation"
                            value={form.password_confirmation}
                            onChange={handleChange}
                            required={!isEdit}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                mt: { xs: 1, md: 3 },
                            }}
                        >
                            {isSuperAdmin && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.is_admin}
                                            name="is_admin"
                                            onChange={handleChange}
                                            size="small"
                                        />
                                    }
                                    label={isArabic ? 'مدير؟' : 'Is admin?'}
                                />
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.status}
                                        name="status"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الأدوار' : 'Roles'}
                            type="multi_select"
                            name="roles_id"
                            selectedValue={form.roles_id}
                            handleSelectChange={handleRolesChange}
                            options={rolesOptions}
                        />
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic
                                ? 'جاري الحفظ...'
                                : 'Saving...'
                            : isEdit
                                ? isArabic
                                    ? 'تحديث'
                                    : 'Update'
                                : isArabic
                                    ? 'حفظ'
                                    : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditUsers;
